import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  message,
  Tabs,
  Table,
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, MinusCircleOutlined, PlusCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getUser, type AdminUserDetail } from '@/api/users';
import { getUserMembership, type UserMembershipDetail } from '@/api/membership';
import { grant, revoke } from '@/api/diamonds';
import { listApps, type App } from '@/api/apps';
import { formatCNY, formatDateTime, truncate } from '@/utils/format';

type Mode = 'grant' | 'revoke';

// 与列表页加钻石一致: 后端 grant/revoke 要求 app_id (钻石按 App 记流水) + 幂等键。
function newIdempotencyKey(userId: string): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `grant-${userId}-${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MembershipCard({ data }: { data: UserMembershipDetail }) {
  const { membership, subscriptions, diamond_lots, orders, topup_transactions } = data;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* 会员状态卡 */}
      <Card
        title={
          <Space>
            {membership.is_member ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            )}
            会员身份
          </Space>
        }
        size="small"
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="是否会员">
            {membership.is_member ? (
              <Tag color="success">会员</Tag>
            ) : (
              <Tag color="default">非会员</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="档位">
            {membership.tier ? <Tag color="blue">{membership.tier}</Tag> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="到期时间">
            {formatDateTime(membership.member_expires_at)}
          </Descriptions.Item>
          <Descriptions.Item label="宽限期截止">
            {formatDateTime(membership.grace_until)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 订阅历史 */}
      <Card title="订阅历史" size="small">
        <Table
          rowKey="id"
          dataSource={subscriptions}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          columns={[
            {
              title: '订阅 ID',
              dataIndex: 'id',
              render: (v: string) => truncate(v, 14),
            },
            {
              title: 'App',
              dataIndex: 'app_id',
              render: (v: number) => <Tag>{v}</Tag>,
            },
            {
              title: 'SKU',
              dataIndex: 'sku_id',
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: string) => {
                const colorMap: Record<string, string> = {
                  active: 'success',
                  cancelled: 'warning',
                  expired: 'default',
                  paused: 'orange',
                };
                return <Tag color={colorMap[v] || 'default'}>{v}</Tag>;
              },
            },
            {
              title: '开始',
              dataIndex: 'started_at',
              render: (v: string) => formatDateTime(v),
            },
            {
              title: '到期',
              dataIndex: 'expires_at',
              render: (v: string) => formatDateTime(v),
            },
            {
              title: '续费次数',
              dataIndex: 'cycle_count',
              align: 'right',
            },
            {
              title: '自动续费',
              dataIndex: 'auto_renew',
              render: (v: boolean) => (v ? <Tag color="blue">是</Tag> : <Tag>否</Tag>),
            },
          ]}
        />
      </Card>

      {/* 钻石批次 */}
      <Card title="钻石批次 (diamond_lots)" size="small">
        <Table
          rowKey="id"
          dataSource={diamond_lots}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          columns={[
            {
              title: '批次 ID',
              dataIndex: 'id',
              render: (v: string) => truncate(v, 16),
            },
            {
              title: '来源',
              dataIndex: 'source_reason',
              render: (v: string) => <Tag>{v}</Tag>,
            },
            {
              title: '初始',
              dataIndex: 'initial',
              align: 'right',
            },
            {
              title: '剩余',
              dataIndex: 'remaining',
              align: 'right',
              render: (v: number, row: { initial: number }) => {
                const pct = row.initial > 0 ? Math.round((v / row.initial) * 100) : 0;
                const color = v === 0 ? 'default' : pct < 30 ? 'warning' : 'success';
                return <Tag color={color}>{v}</Tag>;
              },
            },
            {
              title: '到期',
              dataIndex: 'expire_at',
              render: (v: string | null) => (v ? formatDateTime(v) : '永不过期'),
            },
            {
              title: '宽限截止',
              dataIndex: 'grace_until',
              render: (v: string | null) => formatDateTime(v),
            },
            {
              title: '来源引用',
              dataIndex: 'ref_id',
              render: (v: string | null) => truncate(v, 14),
            },
            {
              title: '入账时间',
              dataIndex: 'created_at',
              render: (v: string) => formatDateTime(v),
            },
          ]}
        />
      </Card>

      {/* 支付/充值流水 */}
      <Card title="订单流水 (最近 50 笔)" size="small">
        <Table
          rowKey="id"
          dataSource={orders}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          columns={[
            {
              title: '订单 ID',
              dataIndex: 'id',
              render: (v: string) => <Link to={`/orders/${v}`}>{truncate(v, 14)}</Link>,
            },
            {
              title: '商户单号',
              dataIndex: 'out_trade_no',
              render: (v: string) => truncate(v, 14),
            },
            {
              title: '渠道',
              dataIndex: 'channel',
              render: (v: string) => <Tag>{v}</Tag>,
            },
            {
              title: '金额',
              dataIndex: 'amount_cents',
              align: 'right',
              render: (v: number) => formatCNY(v),
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: string) => {
                const colorMap: Record<string, string> = {
                  paid: 'success',
                  pending: 'processing',
                  failed: 'error',
                  refunded: 'warning',
                  cancelled: 'default',
                };
                return <Tag color={colorMap[v] || 'default'}>{v}</Tag>;
              },
            },
            {
              title: '支付时间',
              dataIndex: 'paid_at',
              render: (v: string | null) => formatDateTime(v),
            },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              render: (v: string) => formatDateTime(v),
            },
          ]}
        />
      </Card>

      {/* IAP / 充值流水 */}
      <Card title="IAP/充值记录 (topup_transactions, 最近 50 笔)" size="small">
        <Table
          rowKey="id"
          dataSource={topup_transactions}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
            },
            {
              title: '渠道',
              dataIndex: 'provider',
              render: (v: string) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: '交易 ID',
              dataIndex: 'transaction_id',
              render: (v: string) => truncate(v, 18),
            },
            {
              title: '产品 ID',
              dataIndex: 'product_id',
            },
            {
              title: '钻石',
              dataIndex: 'diamonds',
              align: 'right',
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: string) => (
                <Tag color={v === 'completed' ? 'success' : v === 'failed' ? 'error' : 'processing'}>{v}</Tag>
              ),
            },
            {
              title: '时间',
              dataIndex: 'created_at',
              render: (v: string) => formatDateTime(v),
            },
          ]}
        />
      </Card>
    </Space>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [membership, setMembership] = useState<UserMembershipDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // App 选择项 — grant/revoke 必填 app_id。
  const [apps, setApps] = useState<App[]>([]);
  useEffect(() => {
    listApps({ page_size: 100 })
      .then((res) => setApps(res.data))
      .catch(() => {
        /* 非阻塞: 拉不到时仍可手填 App ID */
      });
  }, []);

  const load = () => {
    if (!id) return;
    setLoading(true);
    getUser(id)
      .then(setUser)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  };

  const loadMembership = () => {
    if (!id) return;
    setMembershipLoading(true);
    setMembershipError(null);
    getUserMembership(id)
      .then(setMembership)
      .catch((e) => {
        setMembershipError(e instanceof Error ? e.message : '加载会员数据失败');
      })
      .finally(() => setMembershipLoading(false));
  };

  useEffect(load, [id]);
  useEffect(loadMembership, [id]);

  const onSubmit = async () => {
    if (!user || !mode) return;
    const values = await form.validateFields();
    const payload = {
      app_id: values.app_id,
      amount: values.amount,
      reason: values.reason || (mode === 'grant' ? 'admin_grant' : undefined),
      notes: values.notes || undefined,
      idempotency_key: newIdempotencyKey(user.id),
    };
    setSubmitting(true);
    try {
      if (mode === 'grant') {
        await grant(user.id, payload);
        message.success('已发放钻石');
      } else {
        await revoke(user.id, payload);
        message.success('已扣减钻石');
      }
      setMode(null);
      form.resetFields();
      load();
      loadMembership();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
          返回列表
        </Button>
      </Space>

      {loading || !user ? (
        <Skeleton active />
      ) : (
        <>
          <Card
            extra={
              <Space>
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={() => setMode('grant')}
                >
                  发放钻石
                </Button>
                <Button
                  danger
                  icon={<MinusCircleOutlined />}
                  onClick={() => setMode('revoke')}
                >
                  扣减钻石
                </Button>
              </Space>
            }
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="ID">{user.id}</Descriptions.Item>
              <Descriptions.Item label="昵称">{user.nickname || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机">{user.phone_masked || '-'}</Descriptions.Item>
              <Descriptions.Item label="LTV">{formatCNY(user.lifetime_value_cents)}</Descriptions.Item>
              <Descriptions.Item label="钻石余额">{user.diamond_balance ?? 0}</Descriptions.Item>
              <Descriptions.Item label="注册时间">{formatDateTime(user.created_at)}</Descriptions.Item>
              <Descriptions.Item label="最近活跃">{formatDateTime(user.last_active_at)}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Tabs
            style={{ marginTop: 16 }}
            defaultActiveKey="membership"
            items={[
              {
                key: 'membership',
                label: '会员',
                children: membershipLoading ? (
                  <Skeleton active />
                ) : membershipError ? (
                  <Alert
                    type="error"
                    message="加载会员数据失败"
                    description={membershipError}
                    action={
                      <Button size="small" onClick={loadMembership}>
                        重试
                      </Button>
                    }
                  />
                ) : membership ? (
                  <MembershipCard data={membership} />
                ) : null,
              },
              {
                key: 'apps',
                label: '跨 App 关系',
                children: (
                  <Card>
                    <Table
                      rowKey={(r) => String(r.app_id) + (r.open_id || '')}
                      pagination={false}
                      dataSource={user.app_relations ?? []}
                      columns={[
                        { title: 'App', dataIndex: 'app_slug', render: (v) => <Tag color="blue">{v}</Tag> },
                        { title: 'open_id', dataIndex: 'open_id', render: (v) => truncate(v, 16) },
                        { title: 'union_id', dataIndex: 'union_id', render: (v) => truncate(v, 16) },
                        { title: '邀请人', dataIndex: 'invited_by_user_id', render: (v) => v || '-' },
                        { title: '首次进入', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                      ]}
                    />
                  </Card>
                ),
              },
              {
                key: 'orders',
                label: '订单',
                children: (
                  <Card>
                    <Table
                      rowKey="id"
                      pagination={false}
                      dataSource={user.recent_orders ?? []}
                      columns={[
                        {
                          title: '订单号',
                          dataIndex: 'id',
                          render: (v) => <Link to={`/orders/${v}`}>{truncate(v, 14)}</Link>,
                        },
                        {
                          title: '金额',
                          dataIndex: 'amount_cents',
                          align: 'right',
                          render: (v) => formatCNY(v),
                        },
                        { title: '状态', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
                        { title: '时间', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
                      ]}
                    />
                  </Card>
                ),
              },
              {
                key: 'diamonds',
                label: '钻石流水',
                children: (
                  <Card>
                    <Link to={`/diamonds?user_id=${user.id}`}>
                      查看完整流水 →
                    </Link>
                  </Card>
                ),
              },
            ]}
          />
        </>
      )}

      <Modal
        open={mode !== null}
        title={mode === 'grant' ? '发放钻石' : '扣减钻石'}
        okText="确认"
        okType={mode === 'revoke' ? 'danger' : 'primary'}
        confirmLoading={submitting}
        onCancel={() => setMode(null)}
        onOk={onSubmit}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="app_id"
            label="App"
            rules={[{ required: true, message: '请选择 App (钻石按 App 记流水)' }]}
          >
            <Select
              placeholder="选择 App"
              options={apps.map((a) => ({ value: a.id, label: `${a.name} (#${a.id})` }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="数量"
            rules={[{ required: true, message: '请输入数量' }]}
          >
            <InputNumber min={1} max={100000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label={mode === 'grant' ? '发放原因' : '扣减原因'}
            rules={[{ required: true, message: '请输入原因' }, { max: 64 }]}
          >
            <Input placeholder={mode === 'grant' ? 'admin_grant / event_reward' : 'fraud / mistaken_grant'} />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
